import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import type { RuleGroupType } from "react-querybuilder";
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  type ColumnDef,
  type SortingState,
} from "@tanstack/react-table";
import { useQuery, useMutation, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import type { TableConfig, TableState, ColumnPref, ColumnConfig } from "./types";
import { fetchTableData, fetchColumnPrefs, saveColumnPrefs } from "./api";
import { syncPersistedCache } from "./queryCache";
import { useTableWebSocket } from "./useTableWebSocket";
import RowActions from "./RowActions";
import BulkActions from "./BulkActions";
import ExtraButtons from "./ExtraButtons";
import Pagination from "./Pagination";
import { getColumnType } from "./renderers";
import { getRowEventProps, getCellEventProps, hasCellEvents } from "./events";
import "./events/peers/server";
import "./events/peers/invitelink";
import "./events/addressbook/address";
import SortTooltip from "./SortTooltip";
import ColumnToggle from "./ColumnToggle";
import { AddIcon, DoubleChevronIcon } from "./icons";
import { QueryFilter } from "../query_filter/Filter";
import { getInitialFilterRules } from "../query_filter/filterStorage";
import "./live-table.scss";

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function renderCellValue(value: any, row: Record<string, any>, col: ColumnConfig): React.ReactNode {
  if (value == null) return <span className="text-muted">-</span>;
  const render = getColumnType(col.type);
  if (render) return render(value, row, col.type_config);
  return <>{String(value)}</>;
}

interface Props {
  config: TableConfig;
}

export default function LiveTable({ config }: Props) {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(config.page_size);
  const [search, setSearch] = useState("");
  const [filterRules, setFilterRules] = useState(() =>
    config.has_filter ? getInitialFilterRules() : "",
  );
  const [sorting, setSorting] = useState<SortingState>(config.default_sorting ?? []);
  const [rowSelection, setRowSelection] = useState<Record<string, boolean>>({});
  const [selectAcross, setSelectAcross] = useState(false);
  const [hiddenColumns, setHiddenColumns] = useState<Set<string>>(() => {
    const set = new Set<string>();
    for (const c of config.columns) {
      if (c.hidden) set.add(c.id);
    }
    return set;
  });
  const [columnOrder, setColumnOrder] = useState<string[]>(() =>
    config.columns.map((c) => c.id)
  );
  const [hoveredRowIdx, setHoveredRowIdx] = useState<number | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const tbodyRef = useRef<HTMLTableSectionElement>(null);
  const sidePanelRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    setCanScrollLeft(scrollLeft > 0);
    setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 1);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    updateScrollState();
    el.addEventListener("scroll", updateScrollState);
    const ro = new ResizeObserver(updateScrollState);
    ro.observe(el);
    return () => {
      el.removeEventListener("scroll", updateScrollState);
      ro.disconnect();
    };
  }, [updateScrollState]);

  const scrollBy = useCallback((delta: number) => {
    scrollRef.current?.scrollBy({ left: delta, behavior: "smooth" });
  }, []);

  const ordering = useMemo(() => {
    return sorting.map((s) => (s.desc ? `-${s.id}` : s.id)).join(",");
  }, [sorting]);

  const { data: tableData, isLoading: initialLoading, isFetching } = useQuery({
    queryKey: ["live-table-data", config.api_url, page, pageSize, ordering, search, filterRules],
    queryFn: () => fetchTableData(config.api_url, {
      page,
      pageSize,
      search,
      ordering,
      filter: filterRules,
    }),
    placeholderData: keepPreviousData,
  });

  const lastBulkTotalCountRef = useRef(0);
  useEffect(() => {
    const n = tableData?.total_count;
    if (typeof n === "number" && n > 0) {
      lastBulkTotalCountRef.current = n;
    }
  }, [tableData?.total_count]);

  const handleFilterApply = useCallback((rules: RuleGroupType, quickSearch: string) => {
    const hasRules = rules.rules && rules.rules.length > 0;
    setFilterRules(hasRules ? JSON.stringify(rules) : "");
    setSearch(quickSearch);
    setPage(1);
  }, []);

  const data = useMemo(() => tableData?.results ?? [], [tableData]);

  const queryKey = useMemo(
    () => ["live-table-data", config.api_url, page, pageSize, ordering, search, filterRules],
    [config, page, pageSize, ordering, search, filterRules],
  );

  const visiblePks = useMemo(() => data.map((row) => row.pk), [data]);

  useTableWebSocket({ config, queryKey, visiblePks });

  // Clamp page if server returned empty results but we're past page 1
  useEffect(() => {
    if (tableData && tableData.results.length === 0 && page > 1) {
      const maxPage = Math.max(1, Math.ceil(tableData.total_count / pageSize));
      setPage(maxPage);
    }
  }, [tableData, page, pageSize]);

  useEffect(() => {
    const t = setTimeout(updateScrollState, 50);
    return () => clearTimeout(t);
  }, [data, hiddenColumns, columnOrder, updateScrollState]);

  // Load saved column preferences
  const { data: columnPrefsData } = useQuery({
    queryKey: ["column-prefs", config.model_label],
    queryFn: () => fetchColumnPrefs(config.model_label),
    staleTime: Infinity,
  });

  useEffect(() => {
    if (!columnPrefsData || columnPrefsData.length === 0) return;
    const hidden = new Set<string>();
    for (const p of columnPrefsData) {
      if (!p.show) hidden.add(p.column);
    }
    setHiddenColumns(hidden);

    const savedOrder = [...columnPrefsData]
      .sort((a, b) => a.position - b.position)
      .map((p) => p.column);
    const allIds = config.columns.map((c) => c.id);
    const merged = [
      ...savedOrder.filter((id) => allIds.includes(id)),
      ...allIds.filter((id) => !savedOrder.includes(id)),
    ];
    setColumnOrder(merged);
  }, [columnPrefsData, config.columns]);

  // Sync side panel row heights with actual table row heights
  useEffect(() => {
    const tbody = tbodyRef.current;
    const panel = sidePanelRef.current;
    if (!tbody || !panel) return;

    const sync = () => {
      const tableRows = tbody.querySelectorAll("tr");
      const panelRows = panel.querySelectorAll(".lt-side-panel__row");
      tableRows.forEach((tr, i) => {
        if (panelRows[i]) {
          (panelRows[i] as HTMLElement).style.height = `${tr.offsetHeight}px`;
        }
      });
    };

    sync();

    const ro = new ResizeObserver(sync);
    tbody.querySelectorAll("tr").forEach((tr) => ro.observe(tr));

    return () => ro.disconnect();
  }, [data, initialLoading, hiddenColumns, columnOrder]);

  const saveColumnMutation = useMutation({
    mutationFn: (prefs: ColumnPref[]) => saveColumnPrefs(config.model_label, prefs),
    onSuccess: (_data, prefs) => {
      queryClient.setQueryData(["column-prefs", config.model_label], prefs);
      syncPersistedCache();
    },
  });

  const persistPrefs = useCallback(
    (hidden: Set<string>, order: string[]) => {
      const dataColumns = order.filter(
        (id) => id !== "__select__" && id !== "__actions__",
      );
      const prefs: ColumnPref[] = dataColumns.map((id, idx) => ({
        column: id,
        show: !hidden.has(id),
        position: idx,
      }));
      saveColumnMutation.mutate(prefs);
    },
    [saveColumnMutation],
  );

  const handleToggleColumn = (colId: string) => {
    setHiddenColumns((prev) => {
      const next = new Set(prev);
      if (next.has(colId)) next.delete(colId);
      else next.add(colId);
      persistPrefs(next, columnOrder);
      return next;
    });
  };

  const handleSelectAll = useCallback(() => {
    const all: Record<string, boolean> = {};
    for (const row of data) {
      all[String(row.pk)] = true;
    }
    setRowSelection(all);
    setSelectAcross(true);
  }, [data]);

  const handleClearSelection = useCallback(() => {
    setRowSelection({});
    setSelectAcross(false);
  }, []);

  // Auto-select visible rows when navigating pages while selectAcross is active
  useEffect(() => {
    if (!selectAcross) return;
    const all: Record<string, boolean> = {};
    for (const row of data) {
      all[String(row.pk)] = true;
    }
    setRowSelection(all);
  }, [selectAcross, data]);

  const handleReorderColumns = (fromIdx: number, toIdx: number) => {
    setColumnOrder((prev) => {
      const next = [...prev];
      const [moved] = next.splice(fromIdx, 1);
      next.splice(toIdx, 0, moved);
      persistPrefs(hiddenColumns, next);
      return next;
    });
  };

  const handleResetColumns = useCallback(() => {
    const defaultOrder = config.columns.map((c) => c.id);
    const defaultHidden = new Set<string>();
    for (const c of config.columns) {
      if (c.hidden) defaultHidden.add(c.id);
    }
    setColumnOrder(defaultOrder);
    setHiddenColumns(defaultHidden);
    persistPrefs(defaultHidden, defaultOrder);
  }, [config.columns, persistPrefs]);

  const visibleColumnsConfig = useMemo(() => {
    return columnOrder
      .map((id) => config.columns.find((c) => c.id === id))
      .filter((c) => c && !hiddenColumns.has(c.id) && c.id !== "__actions__") as typeof config.columns;
  }, [config, columnOrder, hiddenColumns]);

  const columns = useMemo<ColumnDef<Record<string, any>>[]>(() => {
    const cols: ColumnDef<Record<string, any>>[] = [];

    if (config.bulk_actions.length > 0) {
      cols.push({
        id: "__select__",
        header: ({ table }) => (
          <input
            type="checkbox"
            className="lt-checkbox"
            checked={table.getIsAllRowsSelected()}
            onChange={table.getToggleAllRowsSelectedHandler()}
          />
        ),
        cell: ({ row }) => (
          <input
            type="checkbox"
            className="lt-checkbox"
            checked={row.getIsSelected()}
            onChange={row.getToggleSelectedHandler()}
          />
        ),
        size: 48,
        enableSorting: false,
      });
    }

    for (const col of visibleColumnsConfig) {
      cols.push({
        id: col.id,
        accessorKey: col.id,
        header: capitalize(col.header),
        cell: ({ row }) => {
          const value = row.original[col.id];
          if (col.is_link && row.original.pk) {
            if (typeof value === "object" && value !== null && value.html) {
              return (
                <a
                  href={`${row.original.pk}/change/`}
                  className="lt-link"
                  dangerouslySetInnerHTML={{ __html: value.html.trim() }}
                />
              );
            }
            const display = typeof value === "object" && value?.str !== undefined
              ? value.str
              : (value ?? "-");
            return (
              <a href={`${row.original.pk}/change/`} className="lt-link">
                {String(display)}
              </a>
            );
          }
          return renderCellValue(value, row.original, col);
        },
        enableSorting: col.sortable,
      });
    }

    return cols;
  }, [visibleColumnsConfig, config.bulk_actions.length]);

  const table = useReactTable({
    data,
    columns,
    state: { sorting, rowSelection },
    onSortingChange: (updater) => {
      setSorting(updater);
      setPage(1);
    },
    onRowSelectionChange: (updater) => {
      setRowSelection(updater);
      setSelectAcross(false);
    },
    getCoreRowModel: getCoreRowModel(),
    manualSorting: true,
    manualPagination: true,
    getRowId: (row) => String(row.pk),
    enableRowSelection: config.bulk_actions.length > 0,
  });

  const showScrollButtons = canScrollLeft || canScrollRight;
  const rows = table.getRowModel().rows;
  const selectedRows = useMemo(
    () => rows.filter((r) => rowSelection[r.id]).map((r) => r.original),
    [rows, rowSelection],
  );

  const tableState = useMemo<TableState>(() => ({
    filterRules,
    search,
    apiUrl: config.api_url,
  }), [filterRules, search, config.api_url]);

  return (
    <div className="lt-wrapper">
      {config.has_filter && (
        <QueryFilter onApply={handleFilterApply} isLoading={isFetching} />
      )}
      <div className="lt-title-bar">
        {config.table_title && (
          <h2 className="lt-title-bar__heading">{config.table_title}</h2>
        )}
        <div className="lt-title-bar__actions">
          {config.extra_buttons && config.extra_buttons.length > 0 && (
            <ExtraButtons buttons={config.extra_buttons} tableState={tableState} />
          )}
          {config.has_add_permission && config.add_url && (
            <a href={config.add_url} className="lt-title-bar__add-btn rd-btn rd-btn--outline rd-btn--primary rd-btn--medium">
              <AddIcon />
              <span>Добавить {config.add_label}</span>
            </a>
          )}
        </div>
      </div>
      <div className="lt-container">
        {/* Scrollable table area */}
        <div className="lt-scroll-area" ref={scrollRef}>
          <table className="lt-table">
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    const isSelect = header.id === "__select__";
                    return (
                      <th
                        key={header.id}
                        className={`${isSelect ? "lt-th--checkbox" : ""} ${header.column.getCanSort() ? "lt-th--sortable" : ""
                          }`}
                      >
                        <div className="lt-th__content">
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {header.column.getCanSort() && (
                            <SortTooltip
                              direction={header.column.getIsSorted()}
                              onSort={(dir) => {
                                setSorting((prev) => {
                                  const without = prev.filter((s) => s.id !== header.column.id);
                                  if (dir === false) return without;
                                  return [{ id: header.column.id, desc: dir === "desc" }, ...without];
                                });
                                setPage(1);
                              }}
                            />
                          )}
                        </div>
                      </th>
                    );
                  })}
                </tr>
              ))}
            </thead>
            <tbody ref={tbodyRef}>
              {initialLoading ? (
                <tr>
                  <td colSpan={columns.length} className="lt-empty">
                    <div className="spinner-border spinner-border-sm" role="status" />
                  </td>
                </tr>
              ) : data.length === 0 ? (
                <tr>
                  <td colSpan={columns.length} className="lt-empty">
                    Нет данных
                  </td>
                </tr>
              ) : (() => {
                const _cellEventsActive = hasCellEvents(config.model_label);
                return rows.map((row, rowIdx) => (
                  <tr
                    key={row.id}
                    className={hoveredRowIdx === rowIdx ? "lt-row--hover" : ""}
                    onMouseEnter={() => setHoveredRowIdx(rowIdx)}
                    onMouseLeave={() => setHoveredRowIdx(null)}
                    {...getRowEventProps(config.model_label, row.original)}
                  >
                    {row.getVisibleCells().map((cell) => {
                      const col = visibleColumnsConfig.find((c) => c.id === cell.column.id);
                      return (
                        <td
                          key={cell.id}
                          className={cell.column.id === "__select__" ? "lt-td--checkbox" : ""}
                          {...(_cellEventsActive && col
                            ? getCellEventProps(config.model_label, row.original, col)
                            : {})}
                        >
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      );
                    })}
                  </tr>
                ));
              })()}
            </tbody>
          </table>
        </div>

        {/* Fixed right panel — actions */}
        <div className="lt-side-panel">
          {/* Header */}
          <div className="lt-side-panel__header">
            {showScrollButtons && (
              <>
                <button
                  type="button"
                  className="lt-scroll-btn"
                  disabled={!canScrollLeft}
                  onClick={() => scrollBy(-300)}
                  title="Прокрутить влево"
                >
                  <DoubleChevronIcon flip />
                </button>
                <button
                  type="button"
                  className="lt-scroll-btn"
                  disabled={!canScrollRight}
                  onClick={() => scrollBy(300)}
                  title="Прокрутить вправо"
                >
                  <DoubleChevronIcon />
                </button>
              </>
            )}
            <ColumnToggle
              columns={config.columns}
              hiddenColumns={hiddenColumns}
              onToggle={handleToggleColumn}
              columnOrder={columnOrder}
              onReorder={handleReorderColumns}
              onReset={handleResetColumns}
            />
          </div>
          {/* Body rows */}
          <div ref={sidePanelRef}>
            {!initialLoading && data.length > 0 && rows.map((row, rowIdx) => (
              <div
                key={row.id}
                className={`lt-side-panel__row${hoveredRowIdx === rowIdx ? " lt-side-panel__row--hover" : ""}`}
                onMouseEnter={() => setHoveredRowIdx(rowIdx)}
                onMouseLeave={() => setHoveredRowIdx(null)}
              >
                <RowActions actions={row.original.actions || []} row={row.original} dropUp={rowIdx >= rows.length - 3} />
              </div>
            ))}
            {(initialLoading || data.length === 0) && (
              <div className="lt-side-panel__row">&nbsp;</div>
            )}
          </div>
        </div>
      </div>

      <Pagination
        page={page}
        totalCount={tableData?.total_count ?? 0}
        totalCountExact={tableData?.total_count_exact ?? true}
        pageSize={pageSize}
        onPageChange={setPage}
        onPageSizeChange={(size: number) => {
          setPageSize(size);
          setPage(1);
        }}
      />

      <BulkActions
        actions={config.bulk_actions}
        selectedRows={selectedRows}
        selectedCount={selectAcross
          ? (tableData?.total_count ?? lastBulkTotalCountRef.current)
          : selectedRows.length}
        totalCount={tableData?.total_count}
        selectAcross={selectAcross}
        tableState={tableState}
        onSelectAll={handleSelectAll}
        onClearSelection={handleClearSelection}
      />
    </div>
  );
}
